import { format, parseISO } from 'date-fns';
import {
  CheckCircle, Hourglass, FilePdf, EnvelopeSimple, CurrencyInr, Trash, Eye,
} from '@phosphor-icons/react';

const fmtInr = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/**
 * Generated-bills table for the Billing page.
 * Pure presentational — parent owns data + action handlers.
 */
export const BillsTable = ({
  bills,
  monthLabel,
  emailConfigured,
  onPreviewPdf,
  onDownloadPdf,
  onEmail,
  onTogglePaid,
  onDelete,
}) => (
  <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
    <table className="w-full tracker-table" data-testid="generated-bills-table">
      <thead>
        <tr>
          <th>Bill #</th>
          <th>Company</th>
          <th>Contact</th>
          <th>Date</th>
          <th>Items</th>
          <th>Total</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {bills.length === 0 ? (
          <tr><td colSpan="8" className="text-center py-6 text-white">No bills generated yet for {monthLabel}.</td></tr>
        ) : (
          bills.map((b) => (
            <BillRow
              key={b.id}
              bill={b}
              emailConfigured={emailConfigured}
              onPreviewPdf={onPreviewPdf}
              onDownloadPdf={onDownloadPdf}
              onEmail={onEmail}
              onTogglePaid={onTogglePaid}
              onDelete={onDelete}
            />
          ))
        )}
      </tbody>
    </table>
  </div>
);

const BillRow = ({ bill: b, emailConfigured, onPreviewPdf, onDownloadPdf, onEmail, onTogglePaid, onDelete }) => (
  <tr data-testid={`generated-bill-${b.id}`}>
    <td className="font-bold">{b.bill_number}</td>
    <td>{b.company_name}</td>
    <td className="text-xs">{b.company_contact || '—'}</td>
    <td>{b.bill_date ? format(parseISO(b.bill_date), 'dd MMM yy') : '—'}</td>
    <td>{b.line_items.length}</td>
    <td className="font-bold">{fmtInr(b.total)}</td>
    <td>
      {b.paid ? (
        <span data-testid={`bill-status-paid-${b.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border-2 border-[#16A34A] bg-[#16A34A] text-white">
          <CheckCircle size={12} weight="fill" />
          PAID{b.paid_at ? ` · ${format(parseISO(b.paid_at.slice(0, 10)), 'dd MMM yy')}` : ''}
        </span>
      ) : (
        <span data-testid={`bill-status-unpaid-${b.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border-2 border-[#FDE047] bg-[#0B2818] text-[#FDE047]">
          <Hourglass size={12} weight="fill" />
          UNPAID
        </span>
      )}
    </td>
    <td>
      <div className="flex gap-1.5">
        <button
          data-testid={`preview-bill-${b.id}`}
          onClick={() => onPreviewPdf(b)}
          className="p-2 bg-[#FDE047] text-black border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
          title="Preview PDF in a new tab"
        >
          <Eye size={14} weight="fill" />
        </button>
        <button
          data-testid={`download-bill-${b.id}`}
          onClick={() => onDownloadPdf(b)}
          className="p-2 bg-[#F87171] text-white border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
          title="Download PDF"
        >
          <FilePdf size={14} weight="fill" />
        </button>
        <button
          data-testid={`email-bill-${b.id}`}
          onClick={() => onEmail(b)}
          disabled={!emailConfigured}
          title={emailConfigured
            ? (b.emailed_at ? `Re-send. Last sent: ${b.emailed_at.slice(0, 10)}` : 'Email this bill PDF to the company')
            : 'Email not configured — set RESEND_API_KEY in backend .env'}
          className={`p-2 border-2 border-black rounded transition-all relative ${
            emailConfigured
              ? 'bg-[#93C5FD] text-black hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]'
              : 'bg-[#666] text-[#aaa] cursor-not-allowed opacity-60'
          }`}
        >
          <EnvelopeSimple size={14} weight="fill" />
          {b.emailed_at && (
            <CheckCircle size={10} weight="fill" className="absolute -top-1 -right-1 text-[#25D366] bg-white rounded-full" data-testid={`emailed-badge-${b.id}`} />
          )}
        </button>
        <button
          data-testid={`toggle-paid-${b.id}`}
          onClick={() => onTogglePaid(b)}
          title={b.paid ? 'Revert to Unpaid' : 'Mark as Paid'}
          className={`p-2 border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all ${b.paid ? 'bg-[#FDE047] text-black' : 'bg-[#16A34A] text-white'}`}
        >
          <CurrencyInr size={14} weight="bold" />
        </button>
        <button
          data-testid={`delete-bill-${b.id}`}
          onClick={() => onDelete(b)}
          className="p-2 bg-[#404040] text-white border-2 border-black rounded hover:-translate-y-0.5 transition-all"
          title="Delete bill (to re-generate)"
        >
          <Trash size={14} weight="bold" />
        </button>
      </div>
    </td>
  </tr>
);
